import React from "react";
import { motion, AnimatePresence } from "framer-motion";

type ModalType = {
  children: React.ReactNode;
  open: boolean;
  onClose?: () => void;
  className?: string;
  verticalOffset?: number;
};

const Modal = React.forwardRef(
  (
    { children, open, onClose, className, verticalOffset }: ModalType,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const modalRef = React.useRef<HTMLDivElement>(null);

    const handleClickOutside = (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        event.preventDefault();
        event.stopPropagation();
        onClose?.();
      }
    };

    React.useEffect(() => {
      if (open) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }

      return () => {
        document.body.style.overflow = "unset";
      };
    }, [open]);

    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape" && open) {
          onClose?.();
        }
      };

      if (open) {
        document.addEventListener("keydown", handleKeyDown);
      }

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [open, onClose]);

    const backdropVariants = {
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
      exit: { opacity: 0 },
    };

    const modalVariants = {
      hidden: {
        opacity: 0,
        scale: 0.75,
        y: 20,
      },
      visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
          type: "spring",
          damping: 25,
          stiffness: 500,
        },
      },
      exit: {
        opacity: 0,
        scale: 0.85,
        y: -20,
        transition: {
          type: "spring",
          damping: 25,
          stiffness: 500,
        },
      },
    };

    return (
      <AnimatePresence mode="wait">
        {open && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 9999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
            }}
          >
            <motion.div
              ref={ref}
              className={`fixed inset-0 bg-black/40 backdrop-blur-sm ${className}`}
              style={{
                pointerEvents: "auto",
                zIndex: 9999999,
              }}
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
              onClick={handleClickOutside}
              onMouseDown={(e) => {
                if (e.target !== e.currentTarget) {
                  e.stopPropagation();
                }
              }}
            />

            <motion.div
              className="pointer-events-auto max-h-[90vh] max-w-[90vw]"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              ref={modalRef}
              style={{
                position: "fixed",
                zIndex: 10000000,
                maxWidth: "90vw",
                maxHeight: "90vh",
                pointerEvents: "auto",
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {children}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    );
  },
);

Modal.displayName = "Modal";

export default Modal;

export const ModalContent = ({
  children,
  className,
  style,
  ref,
  onClick,
  minimized,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  ref?: React.ForwardedRef<HTMLDivElement>;
  minimized?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}) => {
  return (
    <div
      ref={ref}
      className={`z-20 scroll-smooth rounded-none dark:bg-theme-color-light ${
        minimized ? "rounded-lg p-0" : "rounded-none p-4"
      } ${className}`}
      style={style}
      onClick={(event) => {
        event.stopPropagation();
        if (onClick) {
          onClick(event);
        }
      }}
    >
      {children}
    </div>
  );
};
